import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ButtonLoading from '@mui/lab/LoadingButton';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useFormik } from 'formik';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { useNavigate, useParams } from 'react-router-dom';
import { PATH_CONSTANT } from 'constant/PathConstant';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { SUBJECT_CATEGORY, SUBJECT_TYPE } from 'constant/DataInputConstant';
import { ToggleButtonGroupSX } from 'utils/CommonSx';
import {
  SUBJECT_CATEGORY_DROPDOWN,
  SUBJECT_TYPE_DROPDOWN,
  SUBJECT_USER_LIST_FILTER_DROPDOWN,
} from 'constant/SubjectConstant';
import {
  useEditSubject,
  useGetSubjectDetails,
  useGetUserSyncMappings,
  useUpdateUserSyncMappings,
  useUserSyncSourceFieldDropdown,
} from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { SubjectDataUpsertRequest, UserSyncMappingRequestItem } from 'types/api/SystemTenant/AROKMS/SubjectTypes';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTableDefinitionDropdownBySubjectId } from 'services/v1/SystemTenant/AROKMS/TableDefinitionService';
import { RULES_CONSTANT } from 'constant/RuleConstant';

const textInputStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};
const selectedMenuOptionSx = {
  backgroundColor: '#F5F5F5',
  border: '1px solid #42BB93',
  transition: 'ease-in 0.1s',
  '& svg': {
    color: '#42BB93',
  },
};

const iconSx = {
  color: '#828C99',
  fontSize: 40,
};

const menuOptionSx = {
  border: '1px solid #E0E0E0',
  p: 2,
  height: '45px',
  borderRadius: '4px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#F5F5F5',
    border: '1px solid #42BB93',
    transition: 'ease-in 0.1s',
    '& svg': {
      color: '#42BB93',
    },
  },
};

export interface EditSubjectFormikInitValues {
  name: string | null;
  type: AutoCompleteItem | null;
  category: AutoCompleteItem | null;
  categoryFilter?: string | null;
  mappings: UserSyncMappingFormItem[];
}

interface UserSyncMappingFormItem {
  rowId: string;
  tableDefinition: AutoCompleteItem | null;
  sourceField: AutoCompleteItem | null;
}
export const editSubjectDefinitionFormikInitValues: EditSubjectFormikInitValues = {
  name: null,
  type: { label: 'Table', value: SUBJECT_TYPE.TABLE },
  category: { label: 'Regular', value: SUBJECT_CATEGORY.REGULAR },
  categoryFilter: 'ALL_ROLES',
  mappings: [],
};

export const editSubjectValidationSchema = Yup.object().shape({
  name: Yup.string().required('This field is required').nullable(),
  type: Yup.object().required('This field is required').nullable(),
  category: Yup.object().required('This field is required').nullable(),
  categoryFilter: Yup.string().nullable(),
});

function normalizeMappings(mappings: UserSyncMappingRequestItem[]) {
  return [...mappings].sort((left, right) => left.tableDefinitionId.localeCompare(right.tableDefinitionId));
}

export default function EditSubjectDefinition() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subjectId } = useParams<{ subjectId: string }>();

  const { data: subjectDetails, isLoading: isLoadingSubjectDetails } = useGetSubjectDetails(subjectId);

  const { mutateAsync: updateSubject, isLoading: isUpdatingSubject } = useEditSubject();
  const formik = useFormik({
    initialValues: editSubjectDefinitionFormikInitValues,
    validationSchema: editSubjectValidationSchema,
    onSubmit: () => {},
  });

  const originalIsUserListSubject = subjectDetails?.data.category === SUBJECT_CATEGORY.USER_LIST;
  const isUserListSelected = formik.values.category?.value === SUBJECT_CATEGORY.USER_LIST;

  const {
    data: userSyncMappings,
    isLoading: isLoadingUserSyncMappings,
    isError: isUserSyncMappingsError,
  } = useGetUserSyncMappings(subjectId, originalIsUserListSubject);
  const {
    data: sourceFieldDropdown,
    isLoading: isLoadingSourceFieldDropdown,
    isError: isSourceFieldDropdownError,
  } = useUserSyncSourceFieldDropdown(isUserListSelected);
  const {
    data: tableDefinitionDropdown,
    isLoading: isLoadingTableDefinitionDropdown,
    isError: isTableDefinitionDropdownError,
  } = useTableDefinitionDropdownBySubjectId(subjectId, [RULES_CONSTANT.TEXT.RULE_INPUT], isUserListSelected);
  const { mutateAsync: updateUserSyncMappings, isLoading: isUpdatingUserSyncMappings } =
    useUpdateUserSyncMappings(subjectId);
  const initialMappingsRef = useRef<UserSyncMappingRequestItem[] | null>(null);
  const [mappingsInitialized, setMappingsInitialized] = useState(false);

  const mappingPayload: UserSyncMappingRequestItem[] = formik.values.mappings.flatMap((mapping) => {
    if (!mapping.tableDefinition || !mapping.sourceField) return [];
    return [
      {
        tableDefinitionId: mapping.tableDefinition.value.toString(),
        sourceField: mapping.sourceField.value.toString(),
      },
    ];
  });
  const hasCompleteMappings = mappingPayload.length === formik.values.mappings.length;
  const hasUniqueTableDefinitions =
    new Set(mappingPayload.map((mapping) => mapping.tableDefinitionId)).size === mappingPayload.length;
  const hasUniqueSourceFields =
    new Set(mappingPayload.map((mapping) => mapping.sourceField)).size === mappingPayload.length;
  const areMappingsValid = hasCompleteMappings && hasUniqueTableDefinitions && hasUniqueSourceFields;
  const hasMappingLoadError = isUserSyncMappingsError || isSourceFieldDropdownError || isTableDefinitionDropdownError;
  const isLoadingMappings =
    isUserListSelected &&
    ((originalIsUserListSubject && !isUserSyncMappingsError && (!mappingsInitialized || isLoadingUserSyncMappings)) ||
      isLoadingSourceFieldDropdown ||
      isLoadingTableDefinitionDropdown);
  const isSubmitting = isUpdatingSubject || isUpdatingUserSyncMappings;

  const handleOnSubmit = async () => {
    if (subjectId && formik.values.name && formik.values.type && formik.values.category) {
      const payload: SubjectDataUpsertRequest = {
        id: subjectId,
        subject: formik.values.name,
        type: formik.values.type?.value.toString() || undefined,
        category: formik.values.category?.value.toString() || undefined,
        categoryFilter: formik.values.categoryFilter || undefined,
      };
      try {
        await updateSubject(payload);
      } catch (error: any) {
        toast.error(`Failed to edit subject: ${error?.response?.data?.message || error.message}`);
        return;
      }

      const mappingsChanged =
        JSON.stringify(normalizeMappings(initialMappingsRef.current ?? [])) !==
        JSON.stringify(normalizeMappings(mappingPayload));
      const shouldUpdateMappings = isUserListSelected && (!originalIsUserListSubject || mappingsChanged);

      if (shouldUpdateMappings) {
        try {
          await updateUserSyncMappings({ mappings: mappingPayload });
        } catch (error: any) {
          toast.error(
            `Subject details were updated, but failed to update user sync mappings: ${
              error?.response?.data?.message || error.message
            }`
          );
          return;
        }
      }

      navigate(PATH_CONSTANT.SYSTEM.KMS.DATA_BUILDER.SUBJECT.SUBJECT_LIST);
      toast.success('Subject edited successfully');
    }
  };

  const handleOnCancel = () => {
    navigate(PATH_CONSTANT.SYSTEM.KMS.DATA_BUILDER.SUBJECT.SUBJECT_LIST);
  };

  useEffect(() => {
    if (subjectDetails && subjectDetails.data) {
      const selectedCategory =
        SUBJECT_CATEGORY_DROPDOWN.find((option) => option.value === subjectDetails.data.category) || null;

      const selectedType = SUBJECT_TYPE_DROPDOWN.find((option) => option.value === subjectDetails.data.type) || null;

      const selectedCategoryFilter = subjectDetails.data.categoryFilter ? subjectDetails.data.categoryFilter : null;
      formik.setValues({
        name: subjectDetails.data.subject,
        type: selectedType,
        category: selectedCategory,
        categoryFilter: selectedCategoryFilter,
        mappings: formik.values.mappings,
      });

      if (subjectDetails.data.category !== SUBJECT_CATEGORY.USER_LIST && initialMappingsRef.current === null) {
        initialMappingsRef.current = [];
        setMappingsInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectDetails?.data]);

  useEffect(() => {
    if (!userSyncMappings?.data || !sourceFieldDropdown?.data || initialMappingsRef.current !== null) return;

    const mappings = userSyncMappings.data.mappings.map((mapping, index) => ({
      rowId: `mapping-${mapping.tableDefinitionId}-${index}`,
      tableDefinition: {
        value: mapping.tableDefinitionId,
        label: mapping.displayName,
      },
      sourceField: {
        value: mapping.sourceField,
        label:
          sourceFieldDropdown?.data.find((option) => option.value === mapping.sourceField)?.label ??
          mapping.sourceField,
      },
    }));
    const initialMappings = userSyncMappings.data.mappings.map((mapping) => ({
      tableDefinitionId: mapping.tableDefinitionId,
      sourceField: mapping.sourceField,
    }));

    initialMappingsRef.current = initialMappings;
    formik.setFieldValue('mappings', mappings);
    setMappingsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSyncMappings?.data, sourceFieldDropdown?.data]);

  const handleAddMapping = () => {
    formik.setFieldValue('mappings', [
      ...formik.values.mappings,
      {
        rowId: `mapping-new-${Date.now()}-${formik.values.mappings.length}`,
        tableDefinition: null,
        sourceField: null,
      },
    ]);
  };

  const handleRemoveMapping = (rowId: string) => {
    formik.setFieldValue(
      'mappings',
      formik.values.mappings.filter((mapping) => mapping.rowId !== rowId)
    );
  };

  const handleMappingChange = (
    rowId: string,
    field: 'tableDefinition' | 'sourceField',
    value: AutoCompleteItem | null
  ) => {
    formik.setFieldValue(
      'mappings',
      formik.values.mappings.map((mapping) => (mapping.rowId === rowId ? { ...mapping, [field]: value } : mapping))
    );
  };

  const isAllFormValid =
    editSubjectValidationSchema.isValidSync(formik.values) &&
    (!isUserListSelected || (areMappingsValid && !isLoadingMappings && !hasMappingLoadError));
  const isLoadingOptionsData = isLoadingSubjectDetails;

  return (
    <MainLayoutComponent
      pageTitle='Edit Subject Definition'
      breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), 'Subject Definition']}
    >
      <Box p={2} bgcolor='#fff'>
        {isLoadingOptionsData ? (
          <Stack height={450}>
            <ActivityIndicator />
          </Stack>
        ) : (
          <>
            <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
              Edit Subject Definition
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Stack width='100%' direction='column' gap={2} mb={2}>
              <Stack width='40%'>
                <Typography variant='input-label' fontWeight={800}>
                  Subject Name
                </Typography>
                <Typography variant='input-label-gray' fontSize={13}></Typography>
                <TextField
                  sx={textInputStyles}
                  name='name'
                  hiddenLabel
                  value={formik.values.name}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  variant='outlined'
                  size='small'
                  placeholder='e.g. Asset'
                />
              </Stack>
              <Stack width='40%'>
                <Typography variant='input-label' fontWeight={800}>
                  Subject Type
                </Typography>
                <Alert sx={{ my: 1 }} severity='info'>
                  Subject type cannot be changed after creation.
                </Alert>

                <Stack>
                  <ToggleButtonGroup
                    disabled
                    color='primary'
                    value={formik.values.type?.value}
                    exclusive
                    onChange={(_, value) => {
                      const selectedOption = SUBJECT_TYPE_DROPDOWN.find((option) => option.value === value) || null;
                      formik.setFieldValue('type', selectedOption);
                    }}
                    sx={ToggleButtonGroupSX.buttonGroup}
                  >
                    {SUBJECT_TYPE_DROPDOWN.map((option) => (
                      <ToggleButton key={option.value} value={option.value} size='small'>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
              </Stack>
              {/* Show category only if subject type is Table */}
              {formik.values.type?.value === SUBJECT_TYPE.TABLE && (
                <Stack width='40%'>
                  <Typography variant='input-label' fontWeight={800}>
                    Subject Category
                  </Typography>
                  <Typography variant='input-label-gray' fontSize={13}>
                    Select the category for the subject
                  </Typography>
                  <Stack>
                    <Stack direction='row' gap={2} mt={2} width='100%'>
                      {SUBJECT_CATEGORY_DROPDOWN.map((option) => (
                        <Stack
                          key={option.value}
                          width='33%'
                          gap={2}
                          alignItems='center'
                          direction='row'
                          onClick={() => {
                            formik.setFieldValue('category', option);
                          }}
                          sx={{
                            ...menuOptionSx,
                            ...(formik.values.category?.value === option.value ? selectedMenuOptionSx : {}),
                          }}
                        >
                          {option.value === SUBJECT_CATEGORY.REGULAR ? (
                            <TableRowsOutlinedIcon sx={iconSx} />
                          ) : (
                            <GroupOutlinedIcon sx={iconSx} />
                          )}
                          <Stack>
                            <Typography variant='body2' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                              {option.label}
                            </Typography>
                            <Typography variant='input-label-gray' fontWeight={400} component='h2' fontSize={12}>
                              {option.label === 'Regular'
                                ? 'Standard subject for storing data.'
                                : 'Subject used for storing user lists based on roles.'}
                            </Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              )}

              {/* Show category filter only if category is USER_LIST */}
              {formik.values.type?.value === SUBJECT_TYPE.TABLE &&
                formik.values.category?.value === SUBJECT_CATEGORY.USER_LIST && (
                  <Stack width='40%'>
                    <Typography variant='input-label-gray' fontSize={13}>
                      Choose the role filter to apply when synchronizing the user list.
                    </Typography>
                    <RadioGroup
                      aria-labelledby='controlled-radio-buttons-group'
                      name='controlled-radio-buttons-group'
                      value={formik.values.categoryFilter}
                      onChange={(value) => {
                        formik.setFieldValue('categoryFilter', value.target.value);
                      }}
                    >
                      {SUBJECT_USER_LIST_FILTER_DROPDOWN.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio size='small' />}
                          label={
                            <>
                              <Typography fontSize={13}>{option.label}</Typography>
                            </>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Stack>
                )}

              {isUserListSelected && (
                <Stack width='80%' gap={1}>
                  <Typography variant='input-label' fontWeight={800}>
                    User Sync Mappings
                  </Typography>
                  <Typography variant='input-label-gray' fontSize={13}>
                    Map subject table definitions to the fields provided by user synchronization. Each table definition
                    and source field can only be selected once.
                  </Typography>

                  {hasMappingLoadError && (
                    <Alert severity='error'>
                      Unable to load user sync mapping options. Refresh the page and try again.
                    </Alert>
                  )}

                  {!hasMappingLoadError &&
                    (isLoadingMappings ? (
                      <Stack height={120} justifyContent='center'>
                        <ActivityIndicator />
                      </Stack>
                    ) : (
                      <>
                        {formik.values.mappings.map((mapping) => (
                          <Stack key={mapping.rowId} direction='row' gap={2} alignItems='flex-start'>
                            <Autocomplete
                              fullWidth
                              options={tableDefinitionDropdown?.data ?? []}
                              value={mapping.tableDefinition}
                              isOptionEqualToValue={(option, value) =>
                                option.value.toString() === value.value.toString()
                              }
                              getOptionDisabled={(option) =>
                                formik.values.mappings.some(
                                  (item) =>
                                    item.rowId !== mapping.rowId &&
                                    item.tableDefinition?.value.toString() === option.value.toString()
                                )
                              }
                              onChange={(_, value) => handleMappingChange(mapping.rowId, 'tableDefinition', value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label='Table Definition'
                                  size='small'
                                  placeholder='Select a column'
                                />
                              )}
                            />
                            <Autocomplete
                              fullWidth
                              options={sourceFieldDropdown?.data ?? []}
                              value={mapping.sourceField}
                              isOptionEqualToValue={(option, value) =>
                                option.value.toString() === value.value.toString()
                              }
                              getOptionDisabled={(option) =>
                                formik.values.mappings.some(
                                  (item) =>
                                    item.rowId !== mapping.rowId &&
                                    item.sourceField?.value.toString() === option.value.toString()
                                )
                              }
                              onChange={(_, value) => handleMappingChange(mapping.rowId, 'sourceField', value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label='Source Field'
                                  size='small'
                                  placeholder='Select a source field'
                                />
                              )}
                            />
                            <IconButton
                              aria-label='Remove mapping'
                              color='error'
                              onClick={() => handleRemoveMapping(mapping.rowId)}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Stack>
                        ))}

                        {!areMappingsValid && (
                          <Typography color='error' fontSize={12}>
                            Complete every mapping and use each table definition and source field only once.
                          </Typography>
                        )}

                        {formik.values.mappings.length === 0 && (
                          <Typography variant='input-label-gray' fontSize={13}>
                            No mappings configured. Saving this state removes all existing mappings.
                          </Typography>
                        )}

                        <Stack direction='row'>
                          <Button
                            variant='outlined'
                            size='small'
                            startIcon={<AddIcon />}
                            onClick={handleAddMapping}
                            disabled={hasMappingLoadError}
                          >
                            Add Mapping
                          </Button>
                        </Stack>
                      </>
                    ))}
                </Stack>
              )}
            </Stack>
            <Divider sx={{ my: 1 }} />

            <Stack direction='row' px={2} justifyContent='space-between' alignItems='center'>
              <Stack />
              <Stack direction='row' gap={2}>
                <ButtonLoading disabled={isSubmitting} variant='main-table-panel-border' onClick={handleOnCancel}>
                  Cancel
                </ButtonLoading>
                <ButtonLoading
                  variant='main-table-panel'
                  loading={isSubmitting}
                  onClick={handleOnSubmit}
                  disabled={!isAllFormValid}
                >
                  Save
                </ButtonLoading>
              </Stack>
            </Stack>
          </>
        )}
      </Box>
    </MainLayoutComponent>
  );
}
